/**
 * Traduce una fila de cualquier consolidado (Excel o CSV) al esquema del portal.
 * El mapeo se hace por el NOMBRE del encabezado, no por la posición de la columna,
 * así el archivo puede traer columnas de más, de menos o en otro orden.
 */

const sinTildes = (s) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Cada campo del portal y las variantes de encabezado que lo identifican
const REGLAS = [
  ['documento',        [/^documento$/, /identificacion/, /^cedula$/, /^cc$/, /n.*documento/]],
  ['tipo_documento',   [/tipo.*documento/]],
  ['agente',           [/^ag$/, /^agente$/, /^asesor$/, /^gestor$/]],
  ['estado_contacto',  [/estado\s*i\b/, /contactada/, /estado.*contacto/]],
  ['fecha_gestion',    [/intento\s*1/, /fecha.*gestion/, /primer.*intento/]],
  ['aptitud',          [/estado\s*ii\b/, /apta/, /elegibilidad$/]],
  ['motivo_no_elegible', [/motivo.*no.*elegib/, /motivo.*rechazo/]],
  ['modalidad',        [/donde.*prefiere/, /modalidad/, /lugar.*toma/]],
  ['cita',             [/asignacion.*cita/, /^cita$/, /fecha.*cita/, /agendamiento/]],
  ['estado_tamizaje',  [/estado\s*iii\b/, /tamizada/]],
  ['zona',             [/^zona$/, /urbano.*rural/]],
  ['municipio',        [/municipio/]],
  ['regimen',          [/nombre.*regimen/, /^regimen$/]],
  ['edad',             [/^edad$/]],
  ['telefono',         [/resumen\s*telefono/, /^celular$/, /^telefono$/, /telefono\s*1/]],
  ['correo',           [/correo/, /e-?mail/]],
  ['observaciones',    [/observacion/]],
  ['nombre1',          [/primer.?nombre/]],
  ['nombre2',          [/segundo.?nombre/]],
  ['apellido1',        [/primer.?apellido/]],
  ['apellido2',        [/segundo.?apellido/]],
];

export function mapearEncabezados(encabezados) {
  const mapa = {};
  encabezados.forEach((h, i) => {
    const limpio = sinTildes(h);
    if (!limpio) return;
    for (const [campo, patrones] of REGLAS) {
      if (mapa[campo] !== undefined) continue;
      if (patrones.some((p) => p.test(limpio))) { mapa[campo] = i; break; }
    }
  });
  return mapa;
}

/** Acepta Date, número serial de Excel, "dd/mm/aaaa" o "aaaa-mm-dd" */
export function aFecha(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
  if (typeof v === 'number' && v > 20000 && v < 60000) {
    const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
    return isNaN(d) ? null : d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return null;
}

const may = (v) => (v == null || v === '' ? null : String(v).trim().toUpperCase());
const txt = (v) => (v == null || v === '' ? null : String(v).trim());

const ESTADOS = {
  'CONTACTADA': 'CONTACTADA',
  'NO CONTACTADA': 'NO CONTACTADA',
  'NUMERO ERRADO': 'NUMERO ERRADO',
  'NÚMERO ERRADO': 'NUMERO ERRADO',
  'CONTESTA FAMILIAR': 'CONTESTA FAMILIAR',
};

export function filaARegistro(fila, mapa, campanaId) {
  const g = (campo) => (mapa[campo] === undefined ? null : fila[mapa[campo]]);

  const documento = txt(g('documento'));
  if (!documento) return null;

  const nombre = [g('nombre1'), g('nombre2'), g('apellido1'), g('apellido2')]
    .map(txt).filter(Boolean).join(' ') || null;

  const citaBruta = g('cita');
  const citaFecha = aFecha(citaBruta);
  const citaTexto = citaFecha ? null : may(citaBruta);
  const tieneCita = Boolean(citaFecha || citaTexto);

  const estadoBruto = may(g('estado_contacto'));

  return {
    campana_id: campanaId,
    documento,
    tipo_documento: may(g('tipo_documento')),
    nombre,
    telefono: txt(g('telefono')),
    correo: txt(g('correo')),
    edad: Number.isFinite(Number(g('edad'))) && g('edad') !== null && g('edad') !== ''
      ? Math.trunc(Number(g('edad'))) : null,
    zona: may(g('zona')),
    municipio: may(g('municipio')),
    regimen: may(g('regimen')),
    agente: may(g('agente')),
    estado_contacto: estadoBruto ? (ESTADOS[estadoBruto] || estadoBruto) : null,
    fecha_gestion: aFecha(g('fecha_gestion')),
    aptitud: may(g('aptitud')),
    motivo_no_elegible: txt(g('motivo_no_elegible')),
    modalidad: may(g('modalidad')),
    cita_fecha: citaFecha,
    cita_texto: citaTexto,
    tiene_cita: tieneCita,
    estado_tamizaje: may(g('estado_tamizaje')),
    observaciones: txt(g('observaciones')),
    actualizado_en: new Date().toISOString(),
  };
}

/** Parser de CSV que respeta comillas y detecta si el separador es , o ; */
export function leerCSV(texto) {
  const limpio = texto.replace(/^﻿/, '');
  const primera = limpio.split('\n')[0] || '';
  const sep = (primera.match(/;/g) || []).length > (primera.match(/,/g) || []).length ? ';' : ',';
  const filas = [];
  let campo = '', fila = [], comillas = false;
  for (let i = 0; i < limpio.length; i++) {
    const c = limpio[i];
    if (comillas) {
      if (c === '"' && limpio[i + 1] === '"') { campo += '"'; i++; }
      else if (c === '"') comillas = false;
      else campo += c;
    } else if (c === '"') comillas = true;
    else if (c === sep) { fila.push(campo); campo = ''; }
    else if (c === '\n') { fila.push(campo); filas.push(fila); fila = []; campo = ''; }
    else if (c !== '\r') campo += c;
  }
  if (campo !== '' || fila.length) { fila.push(campo); filas.push(fila); }
  return filas.filter((f) => f.some((c) => String(c).trim() !== ''));
}
